import React, { useState, useEffect } from 'react';
import { Navigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, ArrowLeft, Mail, CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { cn } from '../lib/utils';
import api from '../lib/api';

export default function LoginPage() {
  const { isAuthenticated, login, register } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  const [isRegister, setIsRegister] = useState(searchParams.get('register') === 'true');
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Pick up error from AuthCallback redirect
  useEffect(() => {
    if (location.state?.googleError) {
      setError(location.state.googleError);
      // Clear the state so error doesn't persist on refresh
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  useEffect(() => {
    const resetToken = searchParams.get('reset');
    const verifyToken = searchParams.get('verify');

    if (resetToken) {
      setMode('reset');
    } else if (verifyToken) {
      setLoading(true);
      api.get(`/auth/verify-email?token=${verifyToken}`)
        .then(() => { setSuccess('Email verified successfully! You can now sign in.'); setMode('login'); })
        .catch(err => { setError(err.response?.data?.detail || 'Verification failed'); setMode('login'); })
        .finally(() => setLoading(false));
    } else if (searchParams.get('register') === 'true') {
      setMode('register');
      setIsRegister(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (mode === 'register') setIsRegister(true);
    else if (mode === 'login') setIsRegister(false);
  }, [mode]);

  const [justRegistered, setJustRegistered] = useState(false);

  if (isAuthenticated && mode !== 'reset') {
    if (justRegistered) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'forgot') {
        await api.post('/auth/forgot-password', { email });
        setSuccess('If that email exists, a reset link has been sent.');
      } else if (mode === 'reset') {
        const resetToken = searchParams.get('reset');
        await api.post('/auth/reset-password', { token: resetToken, password: newPassword });
        setSuccess('Password reset! You can now sign in.');
        setMode('login');
        setNewPassword('');
      } else if (isRegister) {
        if (!termsAccepted) {
          setError('You must accept the Terms & Conditions.');
          setLoading(false);
          return;
        }
        await register(email, password, name, termsAccepted);
        setJustRegistered(true);
      } else {
        await login(email, password);
        // Navigation is handled by the <Navigate> component in the render
        // when isAuthenticated becomes true
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
      {/* Left panel — dark branded side */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[42%] bg-zinc-950 relative overflow-hidden flex-col justify-between p-12">
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle, #D94E18 1px, transparent 1px)', backgroundSize: '32px 32px' }}
        />
        
        {/* Logo */}
        <div className="relative z-10">
          <Link to="/" className="inline-flex items-center gap-3 group">
            <div className="w-10 h-10 bg-[#D94E18] rounded-lg flex items-center justify-center transition-transform group-hover:scale-105">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" />
                <rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" />
              </svg>
            </div>
            <span className="text-2xl font-bold text-white">kaya.ai</span>
          </Link>
        </div>

        {/* Middle content */}
        <div className="relative z-10 space-y-8">
          <div>
            <h2 className="text-4xl font-bold text-white leading-tight">
              Finance made<br />
              <span className="text-[#D94E18]">easy</span> with AI
            </h2>
            <p className="text-zinc-400 mt-4 text-base leading-relaxed max-w-sm">
              Upload bank statements, auto-classify transactions, and generate financial reports in seconds.
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: '32 countries', sub: 'Auto tax rates' },
              { label: 'AI classification', sub: 'Double-entry' },
              { label: 'PDF, CSV, Image', sub: 'Multi-format upload' },
              { label: 'P&L, Balance Sheet', sub: 'Instant reports' },
            ].map((item) => (
              <div key={item.label} className="bg-white/[0.04] border border-white/[0.06] rounded-lg p-3.5 backdrop-blur-sm">
                <p className="text-white text-sm font-semibold">{item.label}</p>
                <p className="text-zinc-500 text-xs mt-0.5">{item.sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom */}
        <div className="relative z-10">
          <p className="text-zinc-600 text-xs">Trusted by small businesses worldwide</p>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-16 xl:px-20 bg-white">
        <div className="w-full max-w-[400px] mx-auto">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-9 h-9 bg-[#D94E18] rounded-lg flex items-center justify-center">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" />
                <rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" />
              </svg>
            </div>
            <span className="text-xl font-bold text-zinc-900">kaya.ai</span>
          </div>

          {/* Back link for forgot/reset */}
          {(mode === 'forgot' || mode === 'reset') && (
            <button onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
              className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-600 mb-6" data-testid="back-to-login-btn">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </button>
          )}

          {/* Heading */}
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 tracking-tight">
              {mode === 'forgot' ? 'Reset password' : mode === 'reset' ? 'New password' : isRegister ? 'Get started' : 'Sign in'}
            </h1>
            <p className="text-zinc-400 mt-1.5 text-sm">
              {mode === 'forgot' ? "We'll send you a reset link" : mode === 'reset' ? 'Choose a strong password' : isRegister ? 'Create your free account' : 'Welcome back to kaya.ai'}
            </p>
          </div>

          {/* Alerts */}
          {success && (
            <div className="mb-5 p-3.5 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-2.5" data-testid="success-alert">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-700">{success}</p>
            </div>
          )}
          {error && (
            <div className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2.5" data-testid="error-alert">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Google — first, prominent (login/register only) */}
          {(mode === 'login' || mode === 'register') && (
            <>
              <button type="button" disabled={loading} onClick={handleGoogleSignIn} data-testid="google-sign-in-btn"
                className="w-full h-12 flex items-center justify-center gap-3 border border-zinc-200 rounded-xl bg-white hover:bg-zinc-50 hover:border-zinc-300 transition-all font-semibold text-sm text-zinc-700 shadow-sm">
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-100" /></div>
                <div className="relative flex justify-center text-xs"><span className="bg-white px-4 text-zinc-300 font-medium uppercase tracking-wider">or</span></div>
              </div>
            </>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'forgot' && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-sm font-medium text-zinc-600">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com" required data-testid="email-input"
                    className="h-11 rounded-xl border-zinc-200 focus:ring-[#D94E18] focus:border-[#D94E18]" />
                </div>
                <Button type="submit" disabled={loading} data-testid="submit-btn"
                  className="w-full h-11 bg-[#D94E18] hover:bg-[#C2410C] text-white font-semibold rounded-xl">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                  Send Reset Link
                </Button>
              </>
            )}

            {mode === 'reset' && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="newPassword" className="text-sm font-medium text-zinc-600">New Password</Label>
                  <Input id="newPassword" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters" required minLength={8} data-testid="new-password-input"
                    className="h-11 rounded-xl border-zinc-200 focus:ring-[#D94E18] focus:border-[#D94E18]" />
                </div>
                <Button type="submit" disabled={loading} data-testid="submit-btn"
                  className="w-full h-11 bg-[#D94E18] hover:bg-[#C2410C] text-white font-semibold rounded-xl">
                  {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Reset Password
                </Button>
              </>
            )}

            {(mode === 'login' || mode === 'register') && (
              <>
                {isRegister && (
                  <div className="space-y-1.5">
                    <Label htmlFor="name" className="text-sm font-medium text-zinc-600">Full Name</Label>
                    <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="John Doe" required={isRegister} data-testid="name-input"
                      className="h-11 rounded-xl border-zinc-200 focus:ring-[#D94E18] focus:border-[#D94E18]" />
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-sm font-medium text-zinc-600">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com" required data-testid="email-input"
                    className="h-11 rounded-xl border-zinc-200 focus:ring-[#D94E18] focus:border-[#D94E18]" />
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-sm font-medium text-zinc-600">Password</Label>
                    {!isRegister && (
                      <button type="button" onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }}
                        className="text-xs text-zinc-400 hover:text-[#D94E18] transition-colors" data-testid="forgot-password-btn">
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Input id="password" type={showPassword ? 'text' : 'password'} value={password}
                      onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="password-input"
                      className="h-11 pr-10 rounded-xl border-zinc-200 focus:ring-[#D94E18] focus:border-[#D94E18]" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} data-testid="toggle-password-btn"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {isRegister && (
                  <div className="flex items-start gap-2.5 pt-1">
                    <input id="terms" type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}
                      data-testid="terms-checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-[#D94E18] focus:ring-[#D94E18] cursor-pointer" />
                    <label htmlFor="terms" className="text-xs text-zinc-400 leading-relaxed cursor-pointer">
                      I accept the <Link to="/terms" target="_blank" className="text-[#D94E18] hover:underline">Terms & Conditions</Link>,
                      agree to receive service communications from Kaya,
                      and acknowledge all data and reports remain my sole responsibility.
                    </label>
                  </div>
                )}

                <Button type="submit" disabled={loading} data-testid="submit-btn"
                  className={cn("w-full h-12 bg-[#D94E18] hover:bg-[#C2410C] text-white font-semibold rounded-xl",
                    "transition-all active:scale-[0.98] flex items-center justify-center gap-2 mt-2")}>
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {isRegister ? 'Creating account...' : 'Signing in...'}
                    </span>
                  ) : (
                    <>{isRegister ? 'Create account' : 'Sign in'}<ArrowRight className="w-4 h-4" /></>
                  )}
                </Button>
              </>
            )}
          </form>

          {/* Toggle */}
          {(mode === 'login' || mode === 'register') && (
            <p className="mt-8 text-center text-sm text-zinc-400">
              {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button type="button" data-testid="toggle-auth-mode-btn"
                onClick={() => { setMode(isRegister ? 'login' : 'register'); setIsRegister(!isRegister); setError(''); setSuccess(''); }}
                className="text-[#D94E18] font-semibold hover:underline">
                {isRegister ? 'Sign in' : 'Get started free'}
              </button>
            </p>
          )}

          <div className="mt-8 flex justify-center gap-6">
            <Link to="/terms" className="text-xs text-zinc-300 hover:text-zinc-500 transition-colors">Terms</Link>
            <Link to="/privacy" className="text-xs text-zinc-300 hover:text-zinc-500 transition-colors">Privacy</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
